<script setup>
import {
  ref,
  defineProps,
  defineEmits,
  onMounted,
  reactive,
  computed,
} from "vue";
import { apiQuery } from "@/api/j24api";
import AddCommand from "./AddCommand.vue";
import CommandsList from "./CommandsList.vue";
import { permissionsStore } from "@/stores/permissions";
const permissions = permissionsStore();

const props = defineProps({
  app: Object,
  license: Object,
  apps: Array,
  deviceCommands: Array,
  devices: Array,
});

const readOnlyMode = computed(() => {
  let result =
    permissions.$state &&
    permissions.$state["PatchingV2-Commands"] &&
    permissions.$state["PatchingV2-Commands"].actions.includes("Create");
  return !result;
});

const emit = defineEmits(["UpdateCommands", "CancelAddCommand"]);
const addCommand = ref(false);
const deviceCommands = reactive([]);
const globalCommands = reactive([]);
const configCommands = reactive([]);
const testValue = ref("");
const selectedTab = ref("NewCommand");

const headers = [
  { title: "Type", key: "type" },
  { title: "Version", key: "version" },
  { title: "Scope", key: "scope" },
  { title: "Action", key: "delete" },
];

onMounted(() => {
  testValue.value = commands;
});

const commands = computed(() => {
  let result = [];
  result.push(
    ...props.deviceCommands.filter(
      (c) => c.appId == props.app.id && c.status != "cancelled"
    )
  );
  result.push(...getGlobalCommands());
  result.push(...getConfigCommands());
  result = result.map((cmd) => {
    if (cmd.serial) {
      cmd.scope = "Device " + cmd.serial;
    } else if (cmd.scope == "global") {
      cmd.scope = "Global";
    } else if (cmd.scope == "configuration") {
      cmd.scope = "Configuration " + cmd.groupID;
    }
    return cmd;
  });

  return result;
});

function getConfigCommands() {
  let result = [];
  if (props.license.Configurations?.length) {
    props.license.Configurations.forEach((config) => {
      if (config.Commands?.length) {
        config.Commands.filter((cmd) => {
          if (cmd.appId == props.app.id && cmd.status != "cancelled") {
            result.push(cmd);
          }
        });
      }
    });
  }
  return result;
}

function getGlobalCommands() {
  let result = [];
  if (props.license.GlobalConfiguration?.Commands?.length) {
    props.license.GlobalConfiguration.Commands.filter((cmd) => {
      if (cmd.appId == props.app.id && cmd.status != "cancelled") {
        result.push(cmd);
      }
    });
  }
  return result;
}

const showDeleteDialog = ref(false);
const commandToDelete = ref(null);

function deleteCommand(item) {
  commandToDelete.value = item;
  showDeleteDialog.value = true;
}
const errorOverlay = ref(false);
async function deleteCommandConfirmed(item) {
  // Call API to delete command
  console.log("Deleting command confirmed", item);
  showDeleteDialog.value = false;
  commandToDelete.value = null;
  var body = {
    action: "cancelCommand",
    licenseId: props.license._id,
  };
  if (item.serial) {
    body.id = item._id;
    body.serial = item.serial;
  } else if (item.scope == "Global") {
    body.id = item.uuid;
  } else if (item.groupID) {
    body.groupID = item.groupID;
    body.uuid = item.uuid;
  }
  try {
    let result = await apiQuery("ELA", "patching/patchingManagement", body);
    emit("UpdateCommands");
  } catch (error) {
    console.error("Error cancelling command:", error);
    errorOverlay.value = true;
  }
}
</script>

<template>
  <v-btn color="primary" @click="addCommand = true"
    >Commands{{ commands.length ? ` (${commands.length})` : "" }}</v-btn
  >

  <!-- <v-card>
    <v-card-title class="font-weight-bold"
      ><v-row
        ><v-col cols="auto">Commands</v-col><v-spacer></v-spacer
        ><v-col cols="auto"
          ><v-btn color="primary" @click="addCommand = true">
            <v-icon left>mdi-plus</v-icon>
            Add Command
          </v-btn></v-col
        ></v-row
      >
    </v-card-title>
    <v-data-table
      :headers="headers"
      :items="commands"
      density="compact"
      hide-default-footer
    >
      <template v-slot:[`item.delete`]="{ item }">
        <v-icon color="red" class="pointer" @click="deleteCommand(item)"
          >mdi-delete</v-icon
        >
      </template>
    </v-data-table>
  </v-card> -->

  <v-dialog v-model="addCommand" max-width="800">
    <v-card>
      <v-card-text>
        <v-row>
          <v-col cols="auto">
            <h2>Patch Commands</h2>
          </v-col>
          <v-spacer></v-spacer>
          <v-col cols="auto">
            <v-btn icon @click="addCommand = false" variant="plain">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-col>
        </v-row>

        <v-tabs color="#e7541e" class="" v-model="selectedTab">
          <v-tab v-if="!readOnlyMode" value="NewCommand">New Command</v-tab>
          <v-tab value="ExistingCommands"
            >Existing Commands ({{ commands.length }})</v-tab
          >
        </v-tabs>
        <v-window v-model="selectedTab" class="mt-3">
          <v-window-item value="NewCommand">
            <AddCommand
              :hideTitle="true"
              :selectedLicense="props.license"
              :appsList="props.apps"
              :devices="props.devices"
              :selectedApp="props.app"
              :existingCommands="props.deviceCommands"
              @saved-new-command="emit('UpdateCommands')"
              @cancel-new-command="addCommand = false"
            />
          </v-window-item>

          <v-window-item value="ExistingCommands">
            <CommandsList
              :commands="commands"
              :appsList="props.apps"
              @UpdateCommands="emit('UpdateCommands')"
              :readOnlyMode="readOnlyMode"
            />
          </v-window-item>
        </v-window>
      </v-card-text>
    </v-card>
  </v-dialog>

  <v-dialog v-model="errorOverlay" max-width="400">
    <v-card>
      <v-card-title class="headline">Error deleting command</v-card-title>
      <v-card-text>
        There was an error deleting the command. Please try again.
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn color="grey" variant="text" @click="errorOverlay = false"
          >OK</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-dialog v-model="showDeleteDialog" max-width="400">
    <v-card>
      <v-card-title class="headline">Confirm Delete</v-card-title>
      <v-card-text>
        Are you sure you want to delete this command?
        <div v-if="commandToDelete">
          <div><strong>Type:</strong> {{ commandToDelete.type }}</div>
          <div><strong>Version:</strong> {{ commandToDelete.version }}</div>
          <div><strong>Scope:</strong> {{ commandToDelete.scope }}</div>
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn color="grey" variant="text" @click="showDeleteDialog = false"
          >Cancel</v-btn
        >
        <v-btn
          color="red"
          variant="text"
          @click="deleteCommandConfirmed(commandToDelete)"
          >Delete</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
